import { useState } from 'preact/hooks'
import { Loader2 } from 'lucide-preact'
import { sendingDomainsApi } from '@/api/sendingDomains'
import type { SendingDomainViewDto } from '@/types/sendingDomains'
import { useToast } from '@/hooks/useToast'
import { ToggleRow } from '@/components/common/ToggleRow'
import { getDisplayErrorMessages } from '@/utils/errorHandling'
import { SECONDARY_BUTTON } from './styles'

interface SendingDomainTrackingSectionProps {
  clientCompanyId: string
  domain: SendingDomainViewDto
  onUpdated: (domain: SendingDomainViewDto) => void
}

/**
 * Per-domain open/click tracking config. A custom tracking subdomain routes Resend's tracking links
 * through a subdomain of this domain, so clicks register reliably and deliverability stays higher
 * than the shared tracker (which strict recipient mail security often blocks). The generated CNAME
 * joins the DNS records above to verify. Its heading is the disclosure it sits in.
 */
export function SendingDomainTrackingSection({ clientCompanyId, domain, onUpdated }: SendingDomainTrackingSectionProps) {
  const { showSuccess } = useToast()
  const configured = !!domain.TrackingSubdomain
  const trackingHost = configured ? `${domain.TrackingSubdomain}.${domain.Domain}` : null

  const [subdomain, setSubdomain] = useState(domain.TrackingSubdomain ?? 'links')
  // Opinionated defaults: click on (the reliable signal), open off (Resend flags it inaccurate).
  // Reflect the live config once a subdomain is set.
  const [clickTracking, setClickTracking] = useState(configured ? domain.ClickTrackingEnabled : true)
  const [openTracking, setOpenTracking] = useState(domain.OpenTrackingEnabled)
  const [saving, setSaving] = useState(false)
  // Resend's refusals (the once-a-day change, a subdomain that can't be removed) stay beside the button.
  const [refusal, setRefusal] = useState<string[] | null>(null)

  const handleConfigure = async () => {
    const trimmed = subdomain.trim()
    // Only send the subdomain when it actually changes. Re-sending the current value would spend
    // Resend's once-per-24h change budget on a no-op; a blank/omitted value leaves it untouched.
    const subdomainChanged = trimmed !== (domain.TrackingSubdomain ?? '')
    setSaving(true)
    setRefusal(null)
    const res = await sendingDomainsApi.configureTracking(clientCompanyId, domain.Id, {
      TrackingSubdomain: subdomainChanged ? (trimmed || undefined) : undefined,
      ClickTracking: clickTracking,
      OpenTracking: openTracking,
    })
    setSaving(false)
    if (res.error || !res.data) {
      setRefusal(getDisplayErrorMessages(res.error, 'Tracking could not be configured.'))
      return
    }
    // Reconcile local state with the persisted config (the API lowercases the subdomain).
    setSubdomain(res.data.TrackingSubdomain ?? trimmed)
    setClickTracking(res.data.ClickTrackingEnabled)
    setOpenTracking(res.data.OpenTrackingEnabled)
    onUpdated(res.data)
    showSuccess('Tracking configured', 'Add the new tracking CNAME in the DNS records above; it verifies once it resolves.')
  }

  return (
    <div className="space-y-3">
      <div>
        <p className="max-w-prose text-xs text-slate-600 dark:text-slate-400">
          Route tracking links through a subdomain of this domain so clicks register reliably and deliverability stays
          higher than the shared tracker, which strict recipient mail security often blocks.
        </p>
        {configured ? (
          <p className="mt-2 text-xs text-green-700 dark:text-green-400">
            Live on <code className="font-mono">{trackingHost}</code>.
          </p>
        ) : (
          <p className="mt-2 text-xs text-amber-700 dark:text-amber-400">
            On Resend's shared tracking domain. Clicks may not register for recipients with strict mail security.
          </p>
        )}
      </div>

      <div className="max-w-md space-y-3 pt-1">
        <div className="space-y-1">
          <label htmlFor="tracking-subdomain" className="block text-sm font-medium text-slate-700 dark:text-slate-200">
            Tracking subdomain
          </label>
          <div className="flex items-stretch rounded-lg border border-slate-300 dark:border-slate-600 overflow-hidden focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-primary-500">
            <input
              id="tracking-subdomain"
              type="text"
              value={subdomain}
              onInput={e => setSubdomain((e.target as HTMLInputElement).value)}
              disabled={saving}
              placeholder="links"
              className="flex-1 min-w-0 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-white focus:outline-none disabled:opacity-60"
            />
            <span className="flex items-center px-3 font-mono text-sm text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-700/50 border-l border-slate-200 dark:border-slate-600">
              .{domain.Domain}
            </span>
          </div>
        </div>

        <div>
          <ToggleRow label="Click tracking" checked={clickTracking} onChange={setClickTracking} disabled={saving} />
          <ToggleRow
            label="Open tracking"
            checked={openTracking}
            onChange={setOpenTracking}
            disabled={saving}
            labelAdornment={
              <span className="rounded bg-slate-100 dark:bg-slate-700 px-1.5 py-0.5 text-[10px] font-medium text-slate-600 dark:text-slate-300">
                Not recommended
              </span>
            }
          />
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Open tracking relies on a pixel many clients block, so counts run inaccurate and it can reduce deliverability.
          </p>
        </div>

        <button type="button" onClick={handleConfigure} disabled={saving} className={SECONDARY_BUTTON}>
          {saving && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
          {configured ? 'Update tracking' : 'Enable tracking'}
        </button>
        {refusal && (
          <div role="alert" className="text-sm text-red-700 dark:text-red-400">
            {refusal.map(message => <p key={message}>{message}</p>)}
          </div>
        )}
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Changes apply at most once every 24 hours, and a tracking subdomain can't be removed once set.
        </p>
      </div>
    </div>
  )
}
